import { API_CONFIG } from '../config/api';

/**
 * Visitor country detection via the backend IP geo endpoint.
 *
 * Backend contract:
 *   GET {BASE_URL}/stripe/plans?resolveCountry=1  ->  200  { "countryCode": "JP" }
 *   countryCode = ISO 3166-1 alpha-2, derived from the request IP
 *   (e.g. CF-IPCountry header or a geoip lookup).
 *
 * This is used only to pick a DISPLAY currency — the Stripe charge stays USD.
 * Any failure resolves to '' (unknown) so callers fall back to USD.
 */
const STORAGE_KEY = 'mc_country_code';

let inflight: Promise<string> | null = null;

/** Cached ISO alpha-2 country code (uppercase), or '' if not yet resolved. */
export function getCachedCountryCode(): string {
    try {
        return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch {
        return '';
    }
}

/**
 * Resolve the visitor's country code, caching the result for the session.
 * De-duplicates concurrent callers. Never throws — returns '' on any failure.
 */
export async function resolveCountryCode(): Promise<string> {
    const cached = getCachedCountryCode();
    if (cached) return cached;
    if (inflight) return inflight;

    inflight = (async () => {
        try {
            const response = await fetch(
                `${API_CONFIG.BASE_URL}/stripe/plans?resolveCountry=1`,
                {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) return '';
            const data = await response.json().catch(() => ({}));
            const code = String(data.countryCode || data.country || '')
                .toUpperCase()
                .slice(0, 2);
            if (code) {
                try { sessionStorage.setItem(STORAGE_KEY, code); } catch { /* storage disabled */ }
            }
            return code;
        } catch {
            return '';
        } finally {
            inflight = null;
        }
    })();

    return inflight;
}
